import { sizedUrl } from 'hasResizableImages';
import { memo, useCallback, type AnchorHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { SidebarBoxKey } from 'AdmissionsGuidance/StudentDashboard.types';
import { streamDashboardPath, type StreamForDashboard } from 'Lessons';
import { Text } from 'Text';
import { CourseItemBox } from 'CourseItemBox';
import { animatePathChange } from 'RouteAnimation';
import { RenderMarkdown } from 'MarkdownComponents';
import { type LearnerProject, isCapstoneProject } from 'LearnerProjects';
import { ProjectDirectLinkBox, CapstoneProjectBox } from 'ProjectContentBox';
import Button from 'Button';
import { useNavigationHelper } from 'FrontRoyalAngular';
import { frontRoyalStoreApi } from 'FrontRoyalStore';
import { useScheduleBox } from './useScheduleBox';
import { BasicDesktopBox } from '../../BasicDesktopBox';

const ScheduleBoxLink = ({ children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a {...rest} className="text-primary underline hover:opacity-80" target="_blank" rel="noopener noreferrer">
        {children}
    </a>
);

function ScheduleBoxComponent() {
    const { loadUrl } = useNavigationHelper();
    const { data: publishedStreamIdsWithAllContentStored = {} } =
        frontRoyalStoreApi.usePublishedStreamIdsWithAllContentStoredQuery();
    const {
        currentPeriodTitle,
        currentPeriodDescription,
        formattedCurrentPeriodDates,
        learnerProjects,
        requiredCourses,
        optionalCourses,
        scheduleUrl,
    } = useScheduleBox();

    const handleCoursePress = useCallback((course: StreamForDashboard) => {
        animatePathChange(streamDashboardPath({ streamTitle: course.title, streamId: course.id }));
    }, []);

    const handleSchedulePress = useCallback(() => {
        if (!scheduleUrl) return;
        loadUrl(scheduleUrl, '_blank');
    }, [scheduleUrl, loadUrl]);

    if (!formattedCurrentPeriodDates || !currentPeriodTitle) return null;

    return (
        <ScheduleBoxPresentation
            currentPeriodTitle={currentPeriodTitle}
            currentPeriodDescription={currentPeriodDescription}
            formattedCurrentPeriodDates={formattedCurrentPeriodDates}
            learnerProjects={learnerProjects}
            requiredCourses={requiredCourses}
            optionalCourses={optionalCourses}
            onCoursePress={handleCoursePress}
            scheduleUrl={scheduleUrl}
            onSchedulePress={handleSchedulePress}
            publishedStreamIdsWithAllContentStored={publishedStreamIdsWithAllContentStored}
        />
    );
}

// oxlint-disable-next-line max-lines-per-function
function ScheduleBoxPresentation({
    currentPeriodTitle,
    currentPeriodDescription,
    formattedCurrentPeriodDates,
    learnerProjects,
    requiredCourses,
    optionalCourses,
    onCoursePress,
    scheduleUrl,
    onSchedulePress,
    publishedStreamIdsWithAllContentStored,
}: {
    currentPeriodTitle: string;
    currentPeriodDescription: string;
    formattedCurrentPeriodDates: string;
    learnerProjects: LearnerProject[];
    requiredCourses: StreamForDashboard[];
    optionalCourses: StreamForDashboard[];
    onCoursePress: (course: StreamForDashboard) => void;
    scheduleUrl: string;
    onSchedulePress: () => void;
    publishedStreamIdsWithAllContentStored: Record<string, boolean>;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'scheduleBox' });

    return (
        <BasicDesktopBox
            boxKey={SidebarBoxKey.schedule}
            headerText={currentPeriodTitle}
            subtitle={formattedCurrentPeriodDates}
        >
            {currentPeriodDescription && (
                <Text as="div" color="black" weight="normal" className="mb-2.5 text-base">
                    <RenderMarkdown components={{ a: ScheduleBoxLink }}>{currentPeriodDescription}</RenderMarkdown>
                </Text>
            )}
            {learnerProjects.length > 0 && (
                <div className="mb-5">
                    {learnerProjects.map(project => (
                        <div key={project.id} className="mb-2.5 last:mb-0">
                            {isCapstoneProject(project) ? (
                                <CapstoneProjectBox project={project} fullWidth padding="2.5" titleSize="sm" />
                            ) : (
                                <ProjectDirectLinkBox project={project} fullWidth padding="2.5" titleSize="sm" />
                            )}
                        </div>
                    ))}
                </div>
            )}
            {requiredCourses.length > 0 && (
                <div className="mb-5">
                    <Text color="black" weight="normal" className="mb-2.5 text-base">
                        {t('requiredCourses')}
                    </Text>
                    <div>
                        {requiredCourses.map(course => (
                            <div key={course.id} className="mb-2.5 last:mb-0">
                                <CourseItemBox
                                    courseId={course.id}
                                    title={course.title}
                                    src={sizedUrl(course.image, 100, 100) || ''}
                                    progressInfo={null}
                                    hideEndIcon
                                    onPress={() => onCoursePress(course)}
                                    padding="2.5"
                                    titleSize="sm"
                                    availableOffline={!!publishedStreamIdsWithAllContentStored[course.id]}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {optionalCourses.length > 0 && (
                <div className="mb-5">
                    <Text color="black" weight="normal" className="mb-2.5 text-base">
                        {t('optionalCourses')}
                    </Text>
                    <div>
                        {optionalCourses.map(course => (
                            <div key={course.id} className="mb-2.5 last:mb-0">
                                <CourseItemBox
                                    courseId={course.id}
                                    title={course.title}
                                    src={sizedUrl(course.image, 100, 100) || ''}
                                    progressInfo={null}
                                    hideEndIcon
                                    onPress={() => onCoursePress(course)}
                                    padding="2.5"
                                    titleSize="sm"
                                    availableOffline={!!publishedStreamIdsWithAllContentStored[course.id]}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="flex items-center justify-center">
                <Button
                    onPress={onSchedulePress}
                    color="black"
                    variant="bordered-white-light"
                    height={9}
                    radius="full"
                    fontWeight="normal"
                    isDisabled={!scheduleUrl}
                >
                    {t('viewFullSchedule')}
                </Button>
            </div>
        </BasicDesktopBox>
    );
}

export const ScheduleBox = memo(ScheduleBoxComponent) as unknown as typeof ScheduleBoxComponent;
